import React, { useState, useEffect } from "react";
import { usePostHog } from 'posthog-js/react'

import "./FlipCard.css";
import appLogo from "../assets/app-logo.png";
import illustration from "../assets/illustration.png";
import { useNavigate } from "react-router-dom";
import ForgotPasswordModal from "./ForgotPasswordModal";
import ResetPasswordModal from "./ResetPasswordModal";
import ModernDropdown from "./ModernDropdown";
import getApiUrl from "../utils/api";

const FlipCard = ({ onLogin }) => {
  const ALLOWED_DOMAINS = ["limeroad.com", "vmart.co.in", "vmartretail.com"];
  const getEmailDomain = (val) =>
    String(val || "")
      .toLowerCase()
      .split("@")[1] || "";

  // Login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Signup state
  const [signupForm, setSignupForm] = useState({
    FirstName: "",
    LastName: "",
    email: "",
    password: "",
    department_id: "",
  });
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState("");
  const [signupSuccess, setSignupSuccess] = useState("");
  const [departments, setDepartments] = useState([]);

  // Modal states
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  // Flip card state
  const [isFlipped, setIsFlipped] = useState(false);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);

  const navigate = useNavigate();
  const posthog = usePostHog();

  // Load saved email and remember me preference on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    const savedRemember = localStorage.getItem("rememberMe") === "true";

    if (savedEmail && savedRemember) {
      setEmail(savedEmail);
      setRemember(true);
    }
  }, []);

  // Save email when remember me is checked and email changes
  useEffect(() => {
    if (remember && email) {
      localStorage.setItem("rememberedEmail", email);
    }
  }, [email, remember]);

  // Fetch departments for signup
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await fetch(getApiUrl("api/auth/departments"));
        const data = await res.json();
        setDepartments(Array.isArray(data) ? data : []);
      } catch (err) {
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  // Dynamic features and thoughts about the app
  const appFeatures = [
    {
      title: "Streamline Your Workflow",
      description:
        "Manage tasks, collaborate with teams, and boost productivity with the intuitive RELAY platform.",
      icon: "⚡",
    },
    {
      title: "Smart Task Management",
      description:
        "Organize, prioritize, and track your tasks with intelligent automation and real-time updates.",
      icon: "🎯",
    },
    {
      title: "Team Collaboration",
      description:
        "Work together seamlessly with shared workspaces, instant messaging, and collaborative tools.",
      icon: "🤝",
    },
    {
      title: "Analytics & Insights",
      description:
        "Get valuable insights into your productivity patterns and team performance with detailed analytics.",
      icon: "📊",
    },
  ];

  // Rotate features every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeatureIndex((prev) => (prev + 1) % appFeatures.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [appFeatures.length]);

  // Login handlers
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    posthog.capture('Email Login Attempt', {
      email: email,
    });

    const domain = getEmailDomain(email);
    if (!ALLOWED_DOMAINS.includes(domain)) {
      posthog.capture('Login Validation Error', { email, provider: 'email', reason: 'wrong email domain.' })
      setError(
        "This email domain isn't allowed. Please use your corporate email (limeroad.com, vmart.co.in, vmartretail.com)."
      );
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(getApiUrl("api/auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, remember }),
        credentials: "include",
      });

      const fallbackStream = response.clone();
      let parsedJson = null;
      let rawText = "";
      let hasJson = false;

      try {
        parsedJson = await response.json();
        hasJson = true;
      } catch (jsonError) {
        try {
          rawText = await fallbackStream.text();
        } catch (textError) {
          rawText = "";
        }
      }

      if (response.ok) {
        posthog.capture("User Logged In", {
          method: 'email',
          email: email
        });
        if (hasJson && parsedJson && parsedJson.user) {
          const userDetails = parsedJson.user;
          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("user", JSON.stringify(userDetails));
          if (onLogin) onLogin(userDetails);
          posthog.identify(userDetails?.userId, {
            email: userDetails?.email,
            departmentId: userDetails?.departmentId,
            departmentName: userDetails?.departmentName,
          });
          navigate("/mytask");
        } else {
          try {
            const meRes = await fetch(getApiUrl("api/auth/me"), {
              credentials: "include",
            });
            const meData = await meRes.json();
            if (meRes.ok && meData && meData.user) {
              localStorage.setItem("isLoggedIn", "true");
              localStorage.setItem("user", JSON.stringify(meData.user));

              console.log("meData", meData);
              if (onLogin) onLogin(meData.user);
              navigate("/mytask");
              return;
            }
          } catch (meError) {
            console.warn("/me fallback failed:", meError);
          }
          setError(
            "Login succeeded but response was unexpected. Please refresh the page."
          );
        }
      } else {
        const serverMessage =
          hasJson && parsedJson && parsedJson.message
            ? parsedJson.message
            : rawText
              ? rawText.slice(0, 160)
              : `Login failed with status ${response.status}`;
        setError(serverMessage);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(`Login failed: ${err.message || "Network error"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      posthog?.capture('Google Auth Attempt', { provider: 'google' });
      window.location.href = getApiUrl("api/auth/google/auth");
    } catch (err) {
      posthog?.capture('Google Auth Failed', { provider: 'google', reason: err?.message });
      setError("Google login failed");
      setGoogleLoading(false);
    }
  };

  // Signup handlers
  const handleSignupChange = (e) => {
    setSignupForm({ ...signupForm, [e.target.name]: e.target.value });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError("");
    setSignupSuccess("");

    if (
      !signupForm.FirstName ||
      !signupForm.LastName ||
      !signupForm.email ||
      !signupForm.password
    ) {
      setSignupError("Please fill all required fields.");
      return;
    }

    setSignupLoading(true);
    try {
      const res = await fetch(getApiUrl("api/auth/register"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(signupForm),
      });
      const data = await res.json();
      if (res.ok) {
        setSignupSuccess("Account created. You can now sign in.");
        setTimeout(() => {
          setIsFlipped(false); // Flip back to login
          setSignupForm({
            FirstName: "",
            LastName: "",
            email: "",
            password: "",
            department_id: "",
          });
        }, 1500);
      } else {
        setSignupError(data.message || "Registration failed");
      }
    } catch (err) {
      setSignupError("Network error");
    } finally {
      setSignupLoading(false);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    setError("");
    setSignupError("");
    setSignupSuccess("");
  };

  return (
    <div className="flip-card-container">
      <div className={`flip-card ${isFlipped ? "flipped" : ""}`}>
        {/* Login Side (Front) */}
        <div className="flip-card-inner">
          <div className="flip-card-front">
            <div className="login-container">
              <div className="login-left-panel">
                <div className="login-header">
                  <img
                    src={appLogo}
                    alt="RELAY logo"
                    className="login-logo"
                  />
                  <h1 className="login-title">Welcome Back</h1>
                  <p className="login-subtitle">Sign in to your account</p>
                </div>

                <div className="login-form-container">
                  <form onSubmit={handleLogin} className="login-form">
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="form-input"
                        placeholder="Enter your email"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="form-input"
                        placeholder="Enter your password"
                        required
                      />
                    </div>

                    <div className="form-options">
                      <label className="checkbox-container">
                        <input
                          type="checkbox"
                          checked={remember}
                          onChange={() => {
                            const newRemember = !remember;
                            setRemember(newRemember);
                            if (!newRemember) {
                              localStorage.removeItem("rememberMe");
                              localStorage.removeItem("rememberedEmail");
                            } else {
                              localStorage.setItem("rememberMe", "true");
                              if (email) {
                                localStorage.setItem("rememberedEmail", email);
                              }
                            }
                          }}
                          className="modern-checkbox"
                        />
                        <span className="checkmark"></span>
                        Remember me
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowForgotModal(true)}
                        className="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <button
                      type="submit"
                      disabled={loading}
                      className="login-button"
                    >
                      {loading ? "Signing in..." : "Sign In"}
                    </button>

                    <div className="divider">
                      <span>or</span>
                    </div>

                    <button
                      type="button"
                      onClick={handleGoogleLogin}
                      disabled={googleLoading}
                      className="google-button"
                    >
                      {googleLoading ? (
                        <span className="loading-spinner">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            className="spinner"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="none"
                              strokeDasharray="31.416"
                              strokeDashoffset="31.416"
                            >
                              <animate
                                attributeName="stroke-dasharray"
                                dur="2s"
                                values="0 31.416;15.708 15.708;0 31.416"
                                repeatCount="indefinite"
                              />
                              <animate
                                attributeName="stroke-dashoffset"
                                dur="2s"
                                values="0;-15.708;-31.416"
                                repeatCount="indefinite"
                              />
                            </circle>
                          </svg>
                        </span>
                      ) : (
                        <>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            className="google-icon"
                          >
                            <path
                              fill="#4285F4"
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                              fill="#34A853"
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                              fill="#EA4335"
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                          </svg>
                          Continue with Google
                        </>
                      )}
                    </button>

                    <div className="signup-link">
                      Don't have an account?{" "}
                      <button
                        type="button"
                        onClick={handleFlip}
                        className="flip-link"
                      >
                        Create one
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="login-right-panel">
                <div className="illustration-container">
                  <img
                    src={illustration}
                    alt="Task Management"
                    className="modern-illustration"
                  />
                  <div className="illustration-overlay">
                    <div className="feature-icon">
                      {appFeatures[currentFeatureIndex].icon}
                    </div>
                    <h2 className="feature-title">
                      {appFeatures[currentFeatureIndex].title}
                    </h2>
                    <p className="feature-description">
                      {appFeatures[currentFeatureIndex].description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Signup Side (Back) */}
          <div className="flip-card-back">
            <div className="signup-container">
              <div className="signup-header">
                <img src={appLogo} alt="RELAY logo" className="signup-logo" />
                <h1 className="signup-title">Create an account</h1>
                <p className="signup-subtitle">Sign up to continue</p>
              </div>

              <div className="signup-form-container">
                {signupError && (
                  <div className="signup-alert signup-error">{signupError}</div>
                )}
                {signupSuccess && (
                  <div className="signup-alert signup-success">
                    {signupSuccess}
                  </div>
                )}

                <form onSubmit={handleSignup} className="signup-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">First name</label>
                      <input
                        name="FirstName"
                        onChange={handleSignupChange}
                        value={signupForm.FirstName}
                        placeholder="First name"
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last name</label>
                      <input
                        name="LastName"
                        onChange={handleSignupChange}
                        value={signupForm.LastName}
                        placeholder="Last name"
                        className="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      onChange={handleSignupChange}
                      value={signupForm.email}
                      placeholder="you@company.com"
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      name="password"
                      onChange={handleSignupChange}
                      value={signupForm.password}
                      placeholder="********"
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Department (optional)</label>
                    <ModernDropdown
                      name="department_id"
                      options={departments}
                      value={signupForm.department_id}
                      onChange={handleSignupChange}
                      placeholder="Select Department"
                      className="form-input"
                    />
                  </div>

                  <button
                    type="submit"
                    className="signup-button"
                    disabled={signupLoading}
                  >
                    {signupLoading ? "Creating..." : "Sign Up"}
                  </button>

                  <div className="login-link">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={handleFlip}
                      className="flip-link"
                    >
                      Sign in
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ForgotPasswordModal
        show={showForgotModal}
        onClose={() => setShowForgotModal(false)}
        onEmailSubmitted={(email) => {
          setResetEmail(email);
          setShowForgotModal(false);
          setShowResetModal(true);
        }}
      />

      <ResetPasswordModal
        show={showResetModal}
        onClose={() => setShowResetModal(false)}
        email={resetEmail}
      />

      {/* Footer */}
      <footer className="page-footer">
        <div className="footer-content">
          <div className="footer-left">
            <span>© 2024 RELAY. All rights reserved.</span>
            <div className="footer-divider"></div>
            <a
              className="footer-link"
              href="https://help.relay.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Need Help?
            </a>
            <a
              className="footer-link"
              href="https://docs.relay.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Documentation
            </a>
            <a className="footer-link" href="mailto:support@relay.com">
              Contact Support
            </a>
            <a
              className="footer-link"
              href="https://status.relay.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              System Status
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FlipCard;
